import { FIXED_TIMESTEP, MAX_DELTA_TIME } from '@/utils/constants';

type UpdateCallback = (deltaTime: number) => void;
type RenderCallback = (interpolation: number) => void;

/**
 * Fixed timestep game loop with interpolation
 * Runs physics/logic at 60Hz while rendering as fast as possible
 */
export class GameLoop {
  private isRunning = false;
  private lastTime = 0;
  private accumulator = 0;
  private animationFrameId: number | null = null;

  private updateCallbacks: UpdateCallback[] = [];
  private renderCallbacks: RenderCallback[] = [];

  /**
   * Register an update callback (called at fixed 60Hz)
   */
  onUpdate(callback: UpdateCallback): void {
    this.updateCallbacks.push(callback);
  }

  /**
   * Register a render callback (called every frame)
   */
  onRender(callback: RenderCallback): void {
    this.renderCallbacks.push(callback);
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.tick();
  }

  /**
   * Stop the game loop
   */
  stop(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Main loop tick
   */
  private tick = (): void => {
    if (!this.isRunning) return;

    const currentTime = performance.now();
    let deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Clamp delta time to prevent spiral of death
    if (deltaTime > MAX_DELTA_TIME) {
      deltaTime = MAX_DELTA_TIME;
    }

    this.accumulator += deltaTime;

    // Fixed timestep updates
    while (this.accumulator >= FIXED_TIMESTEP) {
      for (const callback of this.updateCallbacks) {
        callback(FIXED_TIMESTEP / 1000); // Convert to seconds
      }
      this.accumulator -= FIXED_TIMESTEP;
    }

    // Calculate interpolation factor for smooth rendering
    const interpolation = this.accumulator / FIXED_TIMESTEP;

    // Render
    for (const callback of this.renderCallbacks) {
      callback(interpolation);
    }

    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  /**
   * Check if the loop is currently running
   */
  get running(): boolean {
    return this.isRunning;
  }
}
